import { useState, useEffect, useCallback } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { get } from "lodash-es";
import { appStore } from "models";

interface Props {
  onPaymentSuccess: (paymentMethod: any) => Promise<void>;
  onError: (error: any) => void;
}

export const useApplePayStripePayment = ({
  onPaymentSuccess,
  onError,
}: Props) => {
  const stripe = useStripe();
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState<any>(null);

  const handlePaymentMethod = useCallback(
    async (event: any) => {
      setIsLoading(true);

      try {
        await onPaymentSuccess(event.paymentMethod);
        event.complete("success");
      } catch (error) {
        onError(error);
        event.complete("fail");
      } finally {
        setIsLoading(false);
      }
    },
    [onPaymentSuccess, onError],
  );

  const handleCancel = useCallback(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const checkApplePayAvailability = async () => {
      if (stripe) {
        const paymentRequest = stripe.paymentRequest({
          country: "US",
          currency: "usd",
          total: {
            label: "Healthi PRO Subscription",
            amount: Math.round(get(appStore.activeStripePlan, "amount", 0)),
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        const result = await paymentRequest.canMakePayment();
        setIsApplePayAvailable(result?.applePay || false);

        if (result?.applePay) {
          setPaymentRequest(paymentRequest);

          // Set up event listeners
          paymentRequest.on("paymentmethod", handlePaymentMethod);
          paymentRequest.on("cancel", handleCancel);
        }
      }
    };

    checkApplePayAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  const handleApplePayPayment = async () => {
    if (!paymentRequest) return;

    setIsLoading(true);

    try {
      await paymentRequest.show();
    } catch (error) {
      onError(error);
      setIsLoading(false);
    }
  };

  return {
    isApplePayAvailable,
    isLoading,
    handleApplePayPayment,
  };
};
